import React, { useEffect, useMemo, useState } from "react";
import "./PhotoGallery.css";

export interface PhotoGalleryProps {
  isOpen: boolean;
  photos: string[];
  initialIndex?: number;
  onClose: () => void;
  showNavigation?: boolean;
}

const clampIndex = (value: number, max: number) => {
  if (max <= 0) return 0;
  if (value < 0) return 0;
  if (value > max - 1) return max - 1;
  return value;
};

const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  isOpen,
  photos,
  initialIndex = 0,
  onClose,
  showNavigation = true,
}) => {
  const safePhotos = useMemo(() => photos.filter(Boolean), [photos]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    setActiveIndex(clampIndex(initialIndex, safePhotos.length));
  }, [isOpen, initialIndex, safePhotos.length]);

  if (!isOpen || safePhotos.length === 0) return null;

  const canGoPrev = activeIndex > 0;
  const canGoNext = activeIndex < safePhotos.length - 1;

  const handlePrev = () => {
    if (!canGoPrev) return;
    setActiveIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    if (!canGoNext) return;
    setActiveIndex((prev) => Math.min(prev + 1, safePhotos.length - 1));
  };

  return (
    <div
      className="photo-gallery-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="photo-gallery-content">
        <button
          className="photo-gallery-close"
          onClick={onClose}
          aria-label="Fechar galeria"
          type="button"
        >
          &times;
        </button>

        <div className="photo-gallery-stage">
          <img
            className="photo-gallery-image"
            src={safePhotos[activeIndex]}
            alt={`Foto ${activeIndex + 1}`}
          />
        </div>

        {showNavigation && (
          <div className="photo-gallery-controls">
            <button
              className="photo-gallery-nav prev"
              onClick={handlePrev}
              disabled={!canGoPrev}
              aria-label="Foto anterior"
              type="button"
            >
              ‹
            </button>

            <div className="photo-gallery-counter">
              {activeIndex + 1}/{safePhotos.length}
            </div>

            <button
              className="photo-gallery-nav next"
              onClick={handleNext}
              disabled={!canGoNext}
              aria-label="Próxima foto"
              type="button"
            >
              ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoGallery;
